import { Popover, Switch } from '@mantine/core';
import { ReactComponent as IconDownload } from './img/iconDownload.svg';
import { ReactComponent as IconComment } from './img/iconComment.svg';
import { ReactComponent as IconClose } from './img/iconClose.svg';
import { ReactComponent as IconKebab } from './img/iconKebab.svg';
import styles from './ContractActions.module.scss';
import { useContractActions } from './hooks/useContractActions';

import { ModalConfirm } from './ModalConfirm';
import { ModalComment } from './ModalComment';
import { getAuthorizationHeader } from '../../redux/Api/contractsApi/contractsApi';
import { getFileNameFromContentDisposition } from '../../utils/strActions';
import { ContractStatus, ContractType } from 'redux/Api/contractsApi/type';
import { Loader } from '@mantine/core';
import { useState } from 'react';

export const ACTION_DOWNLOAD = {
  label: 'download',
  name: 'Договор',
  icon: <IconDownload />,
};

export const ACTION_COMMENT = {
  label: 'comment',
  name: 'Комментарий',
  icon: <IconComment />,
};

export const ACTION_REVOKE = {
  label: 'revoke',
  name: 'Отозвать договор',
  icon: <IconClose />,
};

export const ACTION_TERMINATE = {
  label: 'terminate',
  name: 'Расторгнуть договор',
  icon: <IconClose />,
};

export interface ContractActionsProps {
  contract: ContractType;
  status: ContractStatus;
  isResident: boolean;
}

export const getActionItems = (status: ContractStatus, isResident: boolean) => {
  const items = [];

  items.push(ACTION_DOWNLOAD);

  if (isResident) {
    if (status === 'signed_by_customer') {
      items.push(ACTION_COMMENT);
      items.push(ACTION_REVOKE);
    } else if (status === 'signed_by_worker') {
      items.push(ACTION_COMMENT);
      items.push(ACTION_TERMINATE);
    } else if (status === 'terminated' || status === 'rejected' || status === 'revoked') {
      items.push(ACTION_COMMENT);
    }
  } else {
    if (status === 'signed_by_customer') {
      items.push(ACTION_COMMENT);
      items.push(ACTION_REVOKE);
    } else if (status === 'signed_by_worker') {
      items.push(ACTION_COMMENT);
      items.push(ACTION_TERMINATE);
    } else if (status === 'rejected') {
      items.push(ACTION_COMMENT);
    } else if (status === 'terminated' || status === 'revoked' || status === 'expired') {
      items.push(ACTION_COMMENT);
    }
  }

  return items;
};

export const showMvdNotifications = (status: ContractStatus, isResident: boolean) => {
  return (
    !isResident &&
    (status === 'signed_by_worker' || status === 'terminated' || status === 'expired')
  );
};

export const showTerminationNotification = (
  status: ContractStatus,
  isResident: boolean,
) => {
  return (!isResident && status === 'terminated') || status === 'expired';
};

export const ContractActionsContent: React.FC<{
  status: ContractStatus;
  isResident: boolean;
  onItemClick: (label: string) => void;
  isMvdNotified: boolean;
  isMvdNotifiedOnTermination: boolean;
  onMvdNotificationToggle: (termination?: boolean) => void;
  contractId: number | string;
  isDownloadingContract?: boolean;
}> = ({
  status,
  isResident,
  onItemClick,
  isMvdNotified,
  isMvdNotifiedOnTermination,
  onMvdNotificationToggle,
  contractId,
  isDownloadingContract = false,
}) => {
  const actionItems = getActionItems(status, isResident);
  const [loadingMvdDocType, setLoadingMvdDocType] = useState<12 | 14 | null>(null);

  const handleDownloadMvdDocument = async (docType: 12 | 14) => {
    setLoadingMvdDocType(docType);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/customer/docs/mvd/${docType}/unified-contracts/${contractId}/download`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            Authorization: getAuthorizationHeader(),
          },
        },
      );

      const type = response.headers.get('content-type') || undefined;
      const disposition = response.headers.get('content-disposition');
      const arrayBuffer = await response.blob();

      const blob = new Blob([arrayBuffer], {
        type,
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        getFileNameFromContentDisposition(disposition) || `mvd_document_${docType}.xls`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading MVD document:', error);
    } finally {
      setLoadingMvdDocType(null);
    }
  };

  return (
    <>
      {actionItems.map((item) => {
        return (
          <div
            key={item.label}
            className={styles.item}
            onClick={() =>
              item.label === 'download' && isDownloadingContract
                ? undefined
                : onItemClick(item.label)
            }
            style={
              item.label === 'download' && isDownloadingContract
                ? { cursor: 'default' }
                : undefined
            }>
            {item.icon}
            <span>{item.name}</span>
            {item.label === 'download' && isDownloadingContract && (
              <Loader size='xs' color='#002cfb' style={{ marginLeft: '4px' }} />
            )}
          </div>
        );
      })}

      {showMvdNotifications(status, isResident) && (
        <>
          <div className={styles.divider} />
          <div className={styles.label}>Уведомление МВД</div>
          <div
            className={styles.item}
            onClick={() =>
              loadingMvdDocType === 12 ? undefined : handleDownloadMvdDocument(12)
            }
            style={loadingMvdDocType === 12 ? { cursor: 'default' } : undefined}>
            <IconDownload />
            <span>О заключении</span>
            {loadingMvdDocType === 12 && (
              <Loader size='xs' color='#002cfb' style={{ marginLeft: '4px' }} />
            )}
          </div>
          <div className={styles.item}>
            <Switch
              color='#002CFB'
              checked={isMvdNotified}
              onChange={() => onMvdNotificationToggle()}
              styles={{
                label: {
                  fontSize: '16px',
                  lineHeight: '22px',
                  color: '#121922',
                },
                track: {
                  border: 'none',
                },
                thumb: {
                  border: 'none',
                },
              }}
              label='Уведомил МВД о заключении'
            />
          </div>

          {showTerminationNotification(status, isResident) && (
            <>
              <div
                className={styles.item}
                onClick={() =>
                  loadingMvdDocType === 14 ? undefined : handleDownloadMvdDocument(14)
                }
                style={loadingMvdDocType === 14 ? { cursor: 'default' } : undefined}>
                <IconDownload />
                <span>О расторжении (завершении)</span>
                {loadingMvdDocType === 14 && (
                  <Loader size='xs' color='#002cfb' style={{ marginLeft: '4px' }} />
                )}
              </div>
              <div className={styles.item}>
                <Switch
                  color='#002CFB'
                  checked={isMvdNotifiedOnTermination}
                  onChange={() => onMvdNotificationToggle(true)}
                  styles={{
                    label: {
                      fontSize: '16px',
                      lineHeight: '22px',
                      color: '#121922',
                    },
                  }}
                  label='Уведомил МВД о расторжении'
                />
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

export const ContractActions: React.FC<ContractActionsProps> = ({
  contract,
  status,
  isResident,
}) => {
  const {
    opened,
    setOpened,
    isMvdNotified,
    isMvdNotifiedOnTermination,
    handleItemClick,
    handleMvdNotificationToggle,
    showRevokeModal,
    setShowRevokeModal,
    confirmRevokeContract,
    showTerminateModal,
    setShowTerminateModal,
    confirmTerminateContract,
    showCommentModal,
    setShowCommentModal,
    confirmAddComment,
    showTerminateModalWithOrders,
    setShowTerminateModalWithOrders,
    isDownloadingContract,
  } = useContractActions(contract);
  return (
    <>
      <Popover
        width={330}
        position='bottom-end'
        radius={'4px'}
        opened={opened}
        onChange={setOpened}>
        <Popover.Target>
          <IconKebab
            className={styles.kebabIcon}
            onClick={() => setOpened((prevOpened: boolean) => !prevOpened)}
          />
        </Popover.Target>
        <Popover.Dropdown
          p='4px 0'
          style={{
            borderRadius: '4px',
            boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
            border: 'none',
          }}>
          <ContractActionsContent
            status={status}
            isResident={isResident}
            onItemClick={handleItemClick}
            isMvdNotified={isMvdNotified}
            isMvdNotifiedOnTermination={isMvdNotifiedOnTermination}
            onMvdNotificationToggle={(termination?: boolean) =>
              handleMvdNotificationToggle(termination)
            }
            contractId={contract.id}
            isDownloadingContract={isDownloadingContract}
          />
        </Popover.Dropdown>
      </Popover>
      {showRevokeModal && (
        <ModalConfirm
          setShowLogoutModal={setShowRevokeModal}
          title='Отозвать договор?'
          text={
            isResident
              ? 'Будет создаваться отдельный договор на каждый заказ'
              : 'Ты не сможешь больше отправлять заказы исполнителю'
          }
          confirmText='Отозвать'
          onConfirm={confirmRevokeContract}
        />
      )}

      {showTerminateModal && (
        <ModalConfirm
          setShowLogoutModal={setShowTerminateModal}
          title='Расторгнуть договор?'
          text={
            isResident
              ? 'Будет создаваться отдельный договор на каждый заказ'
              : 'Ты не сможешь больше отправлять заказы исполнителю. Не забудь уведомить МВД в течение 3х дней'
          }
          confirmText='Расторгнуть'
          onConfirm={confirmTerminateContract}
        />
      )}

      {showTerminateModalWithOrders && (
        <ModalConfirm
          setShowLogoutModal={setShowTerminateModalWithOrders}
          title='Расторжение договора'
          text='По данному договору есть опубликованные заказы. Отмени заказы и затем расторгни договор'
          onConfirm={confirmTerminateContract}
        />
      )}

      {showCommentModal && (
        <ModalComment
          setShowCommentModal={setShowCommentModal}
          onConfirm={confirmAddComment}
          currentComment={contract.comment || ''}
        />
      )}
    </>
  );
};
